


import { Component, Prop, Vue } from "vue-property-decorator";

import { CustomNotification } from "@Internal/Models";

import { DesignCategories } from "App";
import { $t, EventBus } from "App/IOC";
import { Activated, Identifiable, Named } from "App/Contracts";
import {
    DesignCategory, DesignCategoryLocales, DesignCategoryRequest, DesignTypes
} from "App/Models/DesignCategory";

import PopupWindow from "Web/Components/Popup/Window.vue";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";

/* AJOUTER OU MODIFIER UNE CATEGORIE — le type, l'ordre, puis le nom dans chaque
   langue du logiciel. Le francais est obligatoire : c'est la langue par defaut de
   l'API, et le nom que voit un utilisateur dont la langue n'est pas remplie. */
@Component({
    components: {
        PopupWindow,
        SimpleSelect
    }
})
export default class DesignCategoryForm extends Vue {
    /* `undefined` : une nouvelle categorie. */
    @Prop()
    readonly Record?: DesignCategory;

    /* Le type filtre dans la liste, propose par defaut a la creation. */
    @Prop( { default: 0 } )
    readonly DefaultTypeId!: number;

    private locales: string[] = DesignCategoryLocales;

    private typeOptions: ( Identifiable & Named )[] = DesignTypes.map( type => ( {
        Id: type.Id,
        Name: $t( `DESIGN_CATEGORIES_type_${type.Id}` )
    } ) );

    private typeId: number = 0;
    private order: string = "";
    private names: { [ locale: string ]: string } = {};

    private saving: boolean = false;

    private action: Identifiable & Named & Activated = {
        Id: "save",
        Name: $t( "DESIGN_CATEGORIES_save" ),
        Active: false
    };

    created(): void {
        const record = this.Record;

        this.typeId = ( record !== undefined )
            ? record.TypeId
            : ( this.DefaultTypeId > 0 ? this.DefaultTypeId : 0 );
        this.order = ( record !== undefined && record.Order !== null )
            ? String( record.Order )
            : "";

        const names: { [ locale: string ]: string } = {};

        this.locales.forEach( ( locale: string ): void => {
            names[ locale ] = ( record !== undefined )
                ? ( record.Translations[ locale ] || "" )
                : "";
        } );

        this.names = names;
        this.refreshAction();
    }

    private get title(): string {
        return this.$t( this.Record !== undefined
            ? "DESIGN_CATEGORIES_editTitle"
            : "DESIGN_CATEGORIES_createTitle" ) as string;
    }

    private get selectedType(): ( Identifiable & Named ) | undefined {
        return this.typeOptions.find( option => option.Id === this.typeId );
    }

    private get orderValue(): number | null {
        const value = this.order.trim();

        return ( value === "" || isNaN( Number( value ) ) ) ? null : Math.round( Number( value ) );
    }

    private get canSave(): boolean {
        return !this.saving && this.typeId > 0 && ( this.names.fr || "" ).trim() !== "";
    }

    private refreshAction(): void {
        this.action.Active = this.canSave;
    }

    private selectType( option: Identifiable & Named ): void {
        this.typeId = Number( option.Id );
        this.refreshAction();
    }

    private async execute(): Promise<void> {
        if( !this.canSave )
            return;

        this.saving = true;
        this.refreshAction();

        const request: DesignCategoryRequest = {
            TypeId: this.typeId,
            Order: this.orderValue,
            Translations: { ...this.names }
        };

        const [ , error ] = ( this.Record !== undefined )
            ? await DesignCategories.Update( this.Record, request )
            : await DesignCategories.Create( request );

        this.saving = false;
        this.refreshAction();

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "DESIGN_CATEGORIES_title" ),
            $t( this.Record !== undefined
                ? "DESIGN_CATEGORIES_updated"
                : "DESIGN_CATEGORIES_created" )
        ));

        this.$emit( "Saved" );
    }
}
